import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

// Criar o contexto
const ProfessionalContext = createContext();

// Hook para facilitar o uso do contexto
export const useProfessionals = () => useContext(ProfessionalContext);

// Provedor do contexto
export const ProfessionalProvider = ({ children, salonId }) => {
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar profissionais ao montar o componente
  useEffect(() => {
    if (!salonId) return;
    
    const fetchProfessionals = async () => {
      try {
        setLoading(true);
        const professionalsRef = collection(db, `salons/${salonId}/professionals`);
        const q = query(professionalsRef, orderBy('nome'));
        
        const snapshot = await getDocs(q);
        
        const professionalsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setProfessionals(professionalsData);
        setError(null);
      } catch (err) {
        console.error('[ProfessionalContext] Erro ao carregar profissionais:', err);
        setError('Falha ao carregar profissionais. Por favor, tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfessionals();
  }, [salonId]);

  // Adicionar um novo profissional
  const addProfessional = async (professionalData) => {
    try {
      const professionalsRef = collection(db, `salons/${salonId}/professionals`);
      const docRef = await addDoc(professionalsRef, {
        ...professionalData,
        createdAt: new Date()
      });
      
      const newProfessional = {
        id: docRef.id,
        ...professionalData,
        createdAt: new Date()
      };
      
      setProfessionals(prev => [...prev, newProfessional].sort((a, b) => a.nome.localeCompare(b.nome)));
      
      return newProfessional;
    } catch (err) {
      console.error('Erro ao adicionar profissional:', err);
      throw err;
    }
  };

  // Atualizar um profissional existente
  const updateProfessional = async (professionalId, professionalData) => {
    try {
      const professionalRef = doc(db, `salons/${salonId}/professionals`, professionalId);
      await updateDoc(professionalRef, {
        ...professionalData,
        updatedAt: new Date()
      });
      
      setProfessionals(prev => prev.map(professional => 
        professional.id === professionalId 
          ? { ...professional, ...professionalData, updatedAt: new Date() } 
          : professional
      ).sort((a, b) => a.nome.localeCompare(b.nome)));
      
      return { id: professionalId, ...professionalData };
    } catch (err) {
      console.error('Erro ao atualizar profissional:', err);
      throw err;
    }
  };

  // Remover um profissional
  const removeProfessional = async (professionalId) => {
    try {
      const professionalRef = doc(db, `salons/${salonId}/professionals`, professionalId);
      await deleteDoc(professionalRef);
      
      setProfessionals(prev => prev.filter(professional => professional.id !== professionalId));
      
      return true;
    } catch (err) {
      console.error('Erro ao remover profissional:', err);
      throw err;
    }
  };

  // Valor do contexto
  const value = {
    professionals,
    loading,
    error,
    addProfessional,
    updateProfessional,
    removeProfessional
  };

  return (
    <ProfessionalContext.Provider value={value}>
      {children}
    </ProfessionalContext.Provider>
  );
}; 